import { memo, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { usePing } from './pingContext'
import {
  noteFileBytes,
  fsContent,
  OWNER_SENDER,
  type TranscriptBlock,
} from '../transport/api'
import { timeLabel, timeTitle } from './blockTime'
import { agentColor } from '../identity/agentColor'
import { PixelPet } from '../identity/PixelPet'
import { Markdown } from '../chat/Markdown'
import { Icon } from '../ui/Icon'
import { BookmarkButton } from './bookmarks/BookmarkButton'
import { systemNote } from './systemNote'
import { parseInbound } from './inbound'
import { useSharedViewer } from './sharedView'
import { TabFactsContext, rowWords, tabNotice, type BrowserActivity } from './browserActivity'
import { parseNoteFileTags } from '../notes/noteFileTags'
import { parseProjectFileNotices } from '../chat/projectFileNotice'
import { ProjectImage } from '../chat/ProjectImage'
import { AttachmentCard } from '../chat/Attachment'
import { turnProse, turnSummary, worthCollapsing } from './turns'
import { setEveryReply, useEveryReply } from './everyReply'
import { useBlobUrl } from '../ui/useBlobUrl'
import { downloadBlob } from '../ui/blobUrl'
import { ImageTile, imageMime } from '../ui/ImageTile'
import { useOpenDoc } from '../files/openDoc'
import { YouTubeCards } from '../ui/YouTube'
import { canPlayInApp, youtubeVideo, type YouTubeVideo } from '../ui/youtubeLink'
import { personBackground, personLetter } from '../identity/personAvatar'

/**
 * HOW A TRANSCRIPT BLOCK IS DRAWN — Clean View's rendering half, out of AgentPanel
 * (M6 拆面板, 2026-09-03). Everything here is a pure renderer over TranscriptBlock;
 * the sockets, paging, caching and scroll physics stay in AgentPanel, which is what
 * makes this split a move rather than a redesign.
 *
 * It also ends a REAL circular import: LiveBlock imported `Msg` from AgentPanel while
 * AgentPanel imported LiveBlock. That cycle held because both evaluate lazily enough
 * to dodge the runtime, which is exactly the kind of luck that expires — and it made
 * either file unloadable alone in a test without dragging the other's whole import
 * tree behind it. LiveBlock now takes Msg from here, and the edge points one way.
 */

const isTool = (b: TranscriptBlock) => b.kind === 'tool_use' || b.kind === 'tool_result'
function ThinkingBlock({ b }: { b: TranscriptBlock }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="trun think">
      <button type="button" className="trun-hd" onClick={() => setOpen((v) => !v)}>
        <span className={`sb-chev${open ? ' open' : ''}`} aria-hidden="true" />
        <span className="trun-label">thinking</span>
      </button>
      {open && <pre className="tb-body">{b.text ?? ''}</pre>}
    </div>
  )
}

/**
 * How much of a conversation to open with, and how much to fetch per scroll-back.
 *
 * Opening a long conversation used to fetch every block. The server has taken ?tail=
 * and ?before= since before this view existed and this client asked for neither, so
 * the cost grew with the whole history rather than with what anyone reads first.
 *
 * 80 rather than a screenful: tool calls are ~76% of a coding transcript and collapse
 * into one row per run, so 80 blocks is far fewer than 80 rows. Enough to fill the
 * view and leave somewhere to scroll before the first backfill is needed.
 */
function baseName(path: string): string {
  const parts = path.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? path
}

/**
 * A unified diff, read by its signs.
 *
 * The server flags these with `diff: true` and the client rendered them as flat
 * monospace, so an addition and a removal were the same colour — the one thing a
 * patch is scanned for. Line-by-line rather than a highlighter: the only distinction
 * that matters here is the leading character, and `@@` hunk headers are the map.
 */
function DiffBody({ text }: { text: string }) {
  return (
    <pre className="tb-body diff">
      {text.split('\n').map((line, i) => {
        const c = line[0]
        /*
         * `***` marks a file boundary in the apply_patch format, and in a MULTI-FILE
         * patch those lines are the only record of which files were touched — the
         * server withholds `file` there on purpose, because naming one of seven
         * would be a confident wrong answer. Rendered as context they were
         * indistinguishable from unchanged code, so the information was on screen
         * and unreadable.
         */
        const cls = line.startsWith('***')
          ? 'df'
          : line.startsWith('@@')
            ? 'dh'
            : c === '+'
              ? 'da'
              : c === '-'
                ? 'dr'
                : undefined
        return (
          <span key={i} className={cls}>
            {line}
            {'\n'}
          </span>
        )
      })}
    </pre>
  )
}

/**
 * The clock on a row — one block's, or a folded group's.
 *
 * Renders NOTHING when the group has no stamp, which is a real and permanent state
 * for two of the five transcript sources, not a loading gap. A dash there would
 * make "this source has no clock" look like "this clock is broken".
 *
 * `aria-hidden` because it is an adornment: the row's meaning is its content, and a
 * screen reader walking a folded turn does not want a time read out before every
 * label. The full-precision ISO stays reachable through `title` for anyone lining
 * this up against a log.
 */
function BlockClock({ items }: { items: readonly TranscriptBlock[] }) {
  const label = timeLabel(items)
  if (!label) return null
  return (
    <span className="blk-ts" title={timeTitle(items)}>
      {label}
    </span>
  )
}

/**
 * A run of consecutive tool calls.
 *
 * Folded to one bar — "14 · Read, Bash, Edit · 18:22–18:28" — where it stands on its
 * own in the transcript (a short turn that was not worth collapsing). BARE inside an
 * opened turn: the turn's own control is the one fold, and a second chevron under it
 * made two nested levels of the same gesture (owner, 2026-09-10: "Show the working"
 * 底下又一層 "Read, Bash, Edit" 這兩層巢狀結構有點莫名其妙, 只需要一層). Bare, the
 * calls are the cards themselves, each wearing its name, its file and its own clock.
 */
export function ToolRun({ items, bare = false }: { items: TranscriptBlock[]; bare?: boolean }) {
  const [open, setOpen] = useState(false)
  const failed = items.some((b) => b.error)
  // Names of the actual calls, deduped in order — "Bash, Read" says more about what
  // happened than "6 tool calls" does, at the same width.
  const names = [...new Set(items.filter((b) => b.kind === 'tool_use').map((b) => b.name ?? 'tool'))]
  const label = names.length ? names.slice(0, 3).join(', ') + (names.length > 3 ? '…' : '') : 'tool'
  const calls = items.filter((b) => b.kind === 'tool_use').length || items.length

  const cards = items.map((b, i) => (
    <div className={`tb${b.error ? ' err' : ''}`} key={i}>
      <div className="tb-hd tb-hd-static">
        <span className="tb-kind">
          {b.kind === 'tool_result' ? 'result' : (b.name ?? 'tool')}
        </span>
        {/* WHICH file, which the tool name alone never said. Basename in the
            row because the column is narrow on a phone; full path on hover. */}
        {b.file && (
          <span className="tb-file" title={b.file}>
            {baseName(b.file)}
          </span>
        )}
        <BlockClock items={[b]} />
      </div>
      {b.diff ? <DiffBody text={b.text ?? ''} /> : <pre className="tb-body">{b.text ?? ''}</pre>}
    </div>
  ))

  if (bare) return <div className={`trun bare${failed ? ' err' : ''}`}>{cards}</div>

  return (
    <div className={`trun${failed ? ' err' : ''}`}>
      <button type="button" className="trun-hd" onClick={() => setOpen((v) => !v)}>
        <span className={`sb-chev${open ? ' open' : ''}`} aria-hidden="true" />
        <span className="trun-n">{calls}</span>
        <span className="trun-label">{label}</span>
        <BlockClock items={items} />
      </button>
      {open && cards}
    </div>
  )
}


/**
 * One turn of the agent working, folded to what it SAID.
 *
 * A turn holds everything the agent did between one message to it and the next — in a
 * real transcript here, up to 26 messages and 55 tool calls, median 7 messages. Read
 * top to bottom that means scrolling through the working to reach the answer, and the
 * answer is what the reader came for.
 *
 * WHAT STAYS OUT OF THE FOLD changed on 2026-10-08 (owner: 只要是 agent 他吐出來的對話,
 * 就選擇不要折). Folded, a turn now shows everything the agent said, in order, and
 * folds only the tool calls and the thinking; the switch beside the control goes back
 * to the last reply alone, which was the only form until then (everyReply.ts says why
 * the default moved). Either way the turn's LAST reply is the message that carries the
 * head and the control, and a line says what is folded behind it.
 *
 * The working is kept, not dropped: a turn that showed only what was said with no
 * sign of the steps would read as though the agent answered out of
 * nowhere, and the steps are how you tell a good answer from a lucky one.
 *
 * A turn with nothing said in it still renders its header, because "ran six commands
 * and said nothing" is a real outcome and a silent gap is not the same as an empty
 * one.
 *
 * WHERE THE CONTROL SITS — owner, 2026-09-10: 把 Show the working 移動到 Agent 名稱的
 * 下方, 放置於上方很不直覺. It used to be a row ABOVE the conclusion, which read as a
 * caption floating between the previous message and this one. It is now INSIDE the
 * conclusion, under the agent's name row: the fold belongs to the answer it hides
 * the working of. Opening unfolds the working ABOVE the conclusion, in order, and
 * the control stays where it was pressed — the conclusion is the turn's last prose
 * and keeps its place at the bottom, so nothing jumps under the finger.
 */
type TurnProps = {
  blocks: TranscriptBlock[]
  session: string
  hash: string
  resolve: (label: string) => string | null
  describeHash?: (h: string) => { name: string; machine: string } | null
  describeRoom?: (id: string) => string | null
  ownerName?: string | null
  resolutionKey?: string
  /** Search (owner, 2026-09-11): the blocks that hold the keyword, and whether THIS turn is the one shown. */
  hits?: ReadonlySet<TranscriptBlock>
  current?: boolean
}

/**
 * A TURN DRAWS AGAIN ONLY WHEN IT CHANGED (owner, 2026-11-04: 功能變多了之後 … UI 有時候會變得很卡). Grouping makes a new
 * array for every turn on every window update, so a turn compared by reference drew again each time — every turn of the
 * window, about once a second while the agent wrote, and on every render above (measured on app-dev: ~470 components
 * drawn per update for a 279-block window). An append keeps every block object the window already had, so a turn is
 * the same when its blocks are the same objects in the same order; everything else is compared as memo would.
 */
export function sameTurn(a: TurnProps, b: TurnProps): boolean {
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof TurnProps>) {
    if (k !== 'blocks' && a[k] !== b[k]) return false
  }
  return a.blocks === b.blocks || (a.blocks.length === b.blocks.length && a.blocks.every((x, i) => x === b.blocks[i]))
}

export const Turn = memo(TurnView, sameTurn)

function TurnView({
  blocks,
  session,
  hash,
  resolve,
  describeHash,
  describeRoom,
  ownerName,
  resolutionKey,
  hits,
  current,
}: TurnProps) {
  const [open, setOpen] = useState(false)
  const summary = turnSummary(blocks)
  const box = useRef<HTMLDivElement>(null)
  const every = useEveryReply()
  /*
   * SEARCH: a turn that holds the CURRENT match is outlined and scrolled to by the view,
   * and STAYS FOLDED — owner, 2026-09-12: 搜尋會將 show the working 隱藏的 block 全部展開,
   * 幫我預設不要展開, 除非使用者去點擊. What a search finds is speech (the conclusion
   * is the turn's head, always visible; earlier prose sits under the fold), so the
   * outline on the turn is the answer and the fold stays the reader's. If they do open
   * it while it is current, the first marked step is brought into view (WorkingFold).
   */
  const hasHit = !!hits && blocks.some((b) => hits.has(b))
  const mark = `${hasHit ? ' hit-item' : ''}${current ? ' hit-current' : ''}`

  if (!worthCollapsing(blocks)) {
    const run = renderRun(blocks, session, hash, resolve, describeHash, describeRoom, ownerName, resolutionKey, hits)
    // A flat run has no box of its own; while it is the current match it gets one, so
    // the view has an element to scroll to and outline.
    return current ? <div className="hit-current">{run}</div> : <>{run}</>
  }

  // The working — everything but the conclusion — unfolds UNDER the control, inside
  // the conclusion's own column, so the steps follow the button (owner, 2026-09-10:
  // 他下面的槽狀結構那層也應該連帶著一起過來). The conclusion's text stays last.
  const working = summary ? blocks.filter((x) => x !== summary) : blocks
  /*
   * WHAT STAYS ON SCREEN WHILE FOLDED — owner, 2026-10-08: 只要是 agent 他吐出來的對話,
   * 就選擇不要折. Everything the agent said before its last reply, in order, drawn
   * between the control and that last reply; the tool calls and the thinking are what
   * the fold still holds. With the switch off this is empty and the turn reads as it
   * always did (everyReply.ts has the why).
   *
   * Each is drawn as the last reply is — the same `pmsg-body md` box, so a table or
   * a code block in an earlier reply looks exactly as it would in the final one (the
   * quieter `turn-step` the opened working uses has no table or code styles: it was
   * built for one-line narration between tool calls). Opening replaces this list with
   * the full working, so nothing is ever drawn twice.
   */
  const earlier = summary ? turnProse(working) : []
  const said =
    every && earlier.length ? (
      <div className="turn-said">
        {earlier.map((r, i) => (
          <div key={i} className={`pmsg-body md turn-reply${hits?.has(r) ? ' hit' : ''}`}>
            <MsgBody text={r.text ?? ''} />
            <BookmarkButton b={r} who={session} kind="agent" text={r.text ?? ''} />
          </div>
        ))}
      </div>
    ) : null
  const fold = (
    <WorkingFold
      blocks={blocks}
      working={working}
      replies={earlier.length}
      session={session}
      hash={hash}
      resolve={resolve}
      describeHash={describeHash}
      describeRoom={describeRoom}
      ownerName={ownerName}
      resolutionKey={resolutionKey}
      hits={hits}
      current={current}
      open={open}
      setOpen={setOpen}
      boxRef={box}
      closedNote={
        summary ? said : (
          <div className="turn-silent">
            no reply — {working.length} step{working.length === 1 ? '' : 's'} only
          </div>
        )
      }
    />
  )

  if (!summary) {
    // Nothing said: no message to carry the control, so it is its own row, and the
    // steps unfold under it in the same indented column a conclusion would give them.
    // (While the agent is STILL WRITING, this row is not drawn here at all: the live
    // block takes the tail turn and carries the fold under its own head — AgentPanel.)
    return (
      <div className={`turn${open ? ' open' : ''} silent${mark}`} ref={box}>
        {fold}
      </div>
    )
  }

  return (
    <div className={`turn${open ? ' open' : ''}${mark}`} ref={box}>
      <Msg
        b={summary}
        session={session}
        hash={hash}
        resolve={resolve}
        describeHash={describeHash ?? null}
        describeRoom={describeRoom ?? null}
        ownerName={ownerName ?? null}
        resolutionKey={resolutionKey ?? ''}
        hit={!!hits?.has(summary)}
        belowHead={fold}
      />
    </div>
  )
}

/**
 * The fold itself — "N show the working" and, open, the steps with "hide the
 * working" at their foot — separated from Turn so the SAME control can sit under the
 * head of the message still being written (LiveBlock) as under a finished one.
 * Owner, 2026-09-12: 在 Agent 還在思考 / 修改代碼的時候, Show the working 的位置放錯了,
 * 應該要放在 Agent 頭像的下面.
 *
 * The open state and the box the scrolls aim at belong to the CALLER: the turn's root
 * carries the `open` class and the search outline, and the live block's wrapper is
 * what the reader is looking at while it streams.
 */
export function WorkingFold({
  blocks,
  working,
  session,
  hash,
  resolve,
  describeHash,
  describeRoom,
  ownerName,
  resolutionKey,
  hits,
  current,
  open,
  setOpen,
  boxRef,
  closedNote,
  replies = 0,
}: {
  /** The whole turn — the clock reads all of it, not the steps alone. */
  blocks: TranscriptBlock[]
  /** The steps behind the fold: the turn minus its conclusion. */
  working: TranscriptBlock[]
  session: string
  hash: string
  resolve: (label: string) => string | null
  describeHash?: ((h: string) => { name: string; machine: string } | null) | undefined
  describeRoom?: ((id: string) => string | null) | undefined
  ownerName?: string | null | undefined
  resolutionKey?: string | undefined
  hits?: ReadonlySet<TranscriptBlock> | undefined
  current?: boolean | undefined
  open: boolean
  setOpen: (next: boolean | ((v: boolean) => boolean)) => void
  boxRef: { current: HTMLElement | null }
  /** Drawn in place of the steps while closed: the turn's earlier replies, or the
   *  silent turn's "no reply — N steps only". */
  closedNote?: ReactNode
  /** How many replies the turn holds besides its last — what the switch shows or hides.
   *  None, and there is no switch: it would change nothing about this turn. */
  replies?: number
}) {
  /*
   * A second way to close, at the BOTTOM of the working — owner, 2026-09-10: 展開後
   * 太多細節的時候, 使用者可以從最下面點擊將其收起. Twenty-eight steps open is a
   * screen or three of cards; the control that opened them is off the top by then.
   * Closing from the bottom collapses everything above the reader, so the top
   * control is brought back into view — otherwise the page would appear to jump
   * to some unrelated earlier message.
   */
  const settle = useRef(false)
  const hideFromBottom = () => {
    settle.current = true
    setOpen(false)
  }
  // After the fold has actually collapsed (an effect runs once the DOM has), not on a
  // timer: the guard against heartbeats names every timer primitive in src, and this
  // is a one-shot layout follow-up, not a clock.
  useEffect(() => {
    if (open || !settle.current) return
    settle.current = false
    const hd = boxRef.current?.querySelector('.turn-hd') as HTMLElement | null
    hd?.scrollIntoView?.({ block: 'nearest' })
  }, [open, boxRef])
  // Search: opened by hand while it is the current match, the first marked step comes
  // into view. Never opened by the search itself (owner, 2026-09-12).
  useEffect(() => {
    if (!current || !open) return
    const first = boxRef.current?.querySelector('.hit') as HTMLElement | null
    first?.scrollIntoView?.({ block: 'nearest' })
  }, [current, open, boxRef])
  const steps = working.length
  /*
   * THE SWITCH — owner, 2026-10-08: 在 Show The Working 後面, 加上一個開關. One answer
   * for the whole view (everyReply.ts), so pressing it here re-lays every turn on
   * screen — the ones ABOVE this one included, which would slide the row out from
   * under the finger that pressed it. Where it was is remembered at the press and put
   * back once the page has re-laid: a layout effect, not a timer, so it lands before
   * the frame is painted.
   */
  const every = useEveryReply()
  const sw = useRef<HTMLInputElement>(null)
  const pressedAt = useRef<number | null>(null)
  useLayoutEffect(() => {
    const was = pressedAt.current
    pressedAt.current = null
    const el = sw.current
    if (was === null || !el) return
    let sc: HTMLElement | null = el.parentElement
    while (sc && !/(auto|scroll)/.test(getComputedStyle(sc).overflowY)) sc = sc.parentElement
    if (sc) sc.scrollTop += el.getBoundingClientRect().top - was
  }, [every])
  return (
    <>
      <div className="turn-bar">
        <button
          type="button"
          className="turn-hd"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Hide the working' : `Show all ${steps} step${steps === 1 ? '' : 's'}`}
        >
          <span className={`sb-chev${open ? ' open' : ''}`} aria-hidden="true" />
          <span className="turn-n">{steps}</span>
          {/* Short on purpose — owner, 2026-10-08: 'Show the working' 這一串文字可以改為
              'all' 用來節省空間. The row now carries a switch as well, and on a phone
              the old sentence and the switch did not both fit beside the clock. The
              count before it is what it is all OF; the full sentence is the button's
              accessible name. */}
          <span className="turn-label">{open ? 'hide' : 'all'}</span>
          {/* The turn's clock reads the WHOLE turn, not the summary block alone: a
              turn that ran for half an hour and answered in one line should say so on
              the folded row, where the reader is deciding whether to open it. */}
          <BlockClock items={blocks} />
        </button>
        {/* Only where it has something to show or hide, and only while folded: open,
            everything is already on screen and the switch would move nothing. */}
        {replies > 0 && !open && (
          <input
            ref={sw}
            type="checkbox"
            role="switch"
            className="turn-sw"
            checked={every}
            onChange={(e) => {
              pressedAt.current = e.currentTarget.getBoundingClientRect().top
              setEveryReply(e.currentTarget.checked)
            }}
            aria-label="Show every reply"
            title="Show every reply"
          />
        )}
      </div>
      {open ? (
        <div className="turn-work">
          {renderWorking(working, session, hash, resolve, describeHash, describeRoom, ownerName, resolutionKey, hits)}
          <button type="button" className="turn-ft" onClick={hideFromBottom}>
            <span className="sb-chev open" aria-hidden="true" />
            <span className="turn-label">hide the working</span>
          </button>
        </div>
      ) : (
        (closedNote ?? null)
      )}
    </>
  )
}

/**
 * The steps of a turn, drawn INSIDE the conclusion's column: tool runs and thinking
 * as their usual folded rows, and the agent's earlier prose as bare text — no
 * avatar, no name row, because every step here is by the agent whose name is
 * already at the top of the message. A second full message card per step would
 * make one answer look like five.
 */
function renderWorking(
  blocks: TranscriptBlock[],
  session: string,
  hash: string,
  resolve: (label: string) => string | null,
  describeHash?: (h: string) => { name: string; machine: string } | null,
  describeRoom?: (id: string) => string | null,
  ownerName?: string | null,
  resolutionKey?: string,
  hits?: ReadonlySet<TranscriptBlock>,
): ReactNode[] {
  const out: ReactNode[] = []
  let run: TranscriptBlock[] = []
  const flushTools = (i: number) => {
    // Bare: the turn's control is the one fold; the calls are shown as cards.
    if (run.length) out.push(<ToolRun key={`r${i}`} items={run} bare />)
    run = []
  }
  blocks.forEach((b, i) => {
    if (isTool(b)) {
      run.push(b)
      return
    }
    flushTools(i)
    if (b.kind === 'assistant') {
      out.push(
        <div key={i} className={`turn-step md${hits?.has(b) ? ' hit' : ''}`}>
          <MsgBody text={b.text ?? ''} />
        </div>,
      )
      return
    }
    // Thinking, system notes and anything else: Msg already draws those without a
    // name row, so they read as steps here exactly as they do anywhere.
    out.push(<Msg key={i} b={b} session={session} hash={hash} resolve={resolve} describeHash={describeHash ?? null} describeRoom={describeRoom ?? null} ownerName={ownerName ?? null} resolutionKey={resolutionKey ?? ''} hit={!!hits?.has(b)} />)
  })
  flushTools(blocks.length)
  return out
}

/** The blocks of a turn, with consecutive tool calls still folded into one row. */
function renderRun(
  blocks: TranscriptBlock[],
  session: string,
  hash: string,
  resolve: (label: string) => string | null,
  describeHash?: (h: string) => { name: string; machine: string } | null,
  describeRoom?: (id: string) => string | null,
  ownerName?: string | null,
  resolutionKey?: string,
  hits?: ReadonlySet<TranscriptBlock>,
): ReactNode[] {
  const out: ReactNode[] = []
  let run: TranscriptBlock[] = []
  const flushTools = (i: number) => {
    if (run.length) out.push(<ToolRun key={`r${i}`} items={run} />)
    run = []
  }
  blocks.forEach((b, i) => {
    if (isTool(b)) {
      run.push(b)
      return
    }
    flushTools(i)
    out.push(<Msg key={i} b={b} session={session} hash={hash} resolve={resolve} describeHash={describeHash ?? null} describeRoom={describeRoom ?? null} ownerName={ownerName ?? null} resolutionKey={resolutionKey ?? ''} hit={!!hits?.has(b)} />)
  })
  flushTools(blocks.length)
  return out
}

/**
 * The way into the terminal from Clean View — bottom-left, round, badged when asked.
 *
 * Rendered INSIDE #claudeOverlay rather than positioned to match it. The overlay
 * already computes its box from --sb-w / --pf-w / --foot-total; a button placed by
 * copying those numbers would be a second statement of the same geometry, correct
 * until one of them changed and then floating over the sidebar with nothing failing.
 *
 * ALWAYS PRESENT, badged only when asking. The server recognises approval-shaped
 * prompts and nothing else, so a button that appeared only on detection would leave
 * every other kind of question — pick one of these three files, choose a branch —
 * with no door at all. The badge is the state; the button is the door.
 *
 * It does not open itself, and that is a decision rather than an omission: a sheet
 * that appears over someone mid-sentence takes the keystroke their next character was
 * going to, and its Escape is the same key that cancels a selection.
 */
export function PeekButton({ asking, onPeek }: { asking?: boolean; onPeek: () => void }) {
  return (
    <button
      type="button"
      className={`ap-peek${asking ? ' asking' : ''}`}
      title={asking ? 'This agent is asking something — tap to answer' : 'Show the terminal'}
      aria-label={asking ? 'This agent is asking something' : 'Show the terminal'}
      onClick={onPeek}
    >
      <Icon name="open_in_new" size={18} />
      {/* A dot as well as the colour: colour alone is a state nobody can name, and
          this is the one control that says "something is waiting on you". */}
      {asking && <span className="ap-peek-dot" aria-hidden="true" />}
    </button>
  )
}

/**
 * A message body, with any attached files shown as files.
 *
 * `<note-file:PATH>` is what this app writes into an agent's input when you attach
 * something, so it comes back in the transcript as part of what you said. Left as
 * prose it is a line of absolute path — the one thing you already know, standing where
 * the picture you sent should be.
 *
 * Images render inline, the same call the project chat makes, because an image IS the
 * message far more often than it is an enclosure. Anything else becomes a link: a name
 * and a click beats a path nobody can act on from a browser.
 *
 * A tag whose path this client cannot resolve is still shown, as a plain filename.
 * Dropping it would be worse than showing it — the message said a file went with it,
 * and silence would make it read as though none ever did.
 */
/**
 * An attached image, and what to show when it will not load.
 *
 * `/notes/file/blob` sits INSIDE the vault gate while `/agent/*` sits outside it, so
 * Clean View is fully readable with the vault locked and this one request 401s. The
 * browser's answer to that is a broken-image icon, which says "this file is gone" —
 * the wrong half of the truth, and the half that makes someone go looking for a file
 * that is exactly where they left it.
 *
 * So a failure falls back to the filename and the reason. `onError` rather than
 * reading the lock state, because it is also right for the cases the lock does not
 * cover — a swept blob, a revoked session — and those are indistinguishable from here
 * anyway. The sentence names the likely cause without asserting it.
 */
function MsgImage({ fileId, name }: { fileId: string; name: string }) {
  /*
   * BYTES OVER THE RELAY, not a blob URL — the URL pointed at the page origin,
   * which proxies nothing of the edge's, so every inline image died at the
   * API-shape guard and fell back to the "can't load it" chip (owner,
   * 2026-08-08: 讓 Clean View 可以解析並且繪製圖案標籤的圖案). Same fetch, same
   * object-URL lifecycle as NoteAttachments' ImageAtt — and it WAS the same lifecycle,
   * written out again. Both are useBlobUrl now, which revokes on unmount so a long
   * transcript does not pin every picture for the life of the page.
   */
  /* TYPED, from the name: the slices come back as bare bytes, and a Blob with no type
     is what WebKit downloads instead of showing when the URL is opened anywhere but
     an <img> — the mechanism behind the owner's "點下圖片就自動下載" (2026-09-13). */
  const mime = imageMime(name)
  const { url, failed: fetchFailed } = useBlobUrl(() => noteFileBytes(fileId, mime), [fileId])
  /* TWO ways to fail, and they are not the same event: the bytes never arriving, and
     the browser refusing to decode bytes that did. Both end at the same chip, but only
     the second can happen after a successful fetch. */
  const [decodeFailed, setDecodeFailed] = useState(false)
  const failed = fetchFailed || decodeFailed
  if (failed) {
    return (
      <span className="msg-file off" title={name}>
        <Icon name="attach_file" size={13} />
        {name} — can’t load it
      </span>
    )
  }
  if (!url) return <span className="msg-file off">{name} — loading…</span>
  /* The click goes to the cover-panel viewer (ImageTile asks the OpenDoc context),
     never to a new tab or a download — owner, 2026-09-13. */
  return <ImageTile url={url} name={name} mime={mime} onError={() => setDecodeFailed(true)} />
}

/**
 * An image named by BARE PATH — an agent's own screenshot, a build artifact —
 * with no blob-store id to fetch by. /fs/content already crosses the relay and
 * answers binaries as base64 with the mime declared, so the picture renders
 * from a data URL. Failure keeps the path readable: a line someone can act on
 * beats a broken-image icon.
 */
function PathImage({ path, name }: { path: string; name: string }) {
  const [url, setUrl] = useState<string | null>(null)
  // The mime the machine declared for the file, kept for the viewer — the data URL
  // carries it too, but the viewer decides its kind from the mime it is handed.
  const [mime, setMime] = useState('image/png')
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    fsContent(path)
      .then((r) => {
        if (!alive) return
        if (r.ok && r.encoding === 'base64' && r.data && !r.truncated) {
          setMime(r.mime || 'image/png')
          setUrl(`data:${r.mime || 'image/png'};base64,${r.data}`)
        } else {
          setFailed(true)
        }
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
    }
  }, [path])
  if (failed) {
    return (
      <span className="msg-file off" title={path}>
        <Icon name="attach_file" size={13} />
        {name} — can’t load it
      </span>
    )
  }
  if (!url) return <span className="msg-file off">{name} — loading…</span>
  return <ImageTile url={url} name={name} mime={mime} onError={() => setFailed(true)} />
}

function MsgBody({ text }: { text: string }) {
  const { text: prose, files } = parseNoteFileTags(text)
  // The project notice keeps its sentence — it is prose someone wrote around, and it
  // carries the size and the fact of the share. The picture goes underneath it.
  const shared = parseProjectFileNotices(prose)
  const ping = usePing()
  /*
   * A FRIEND'S VIEW FETCHES NOTHING THESE NAME (review of 2026-10-17). Every fetcher below reads
   * the READER's own machine or account — the relay target, the project store — and a friend's
   * agent's files are on the sharer's: a friend's picture never arrived, and a bare path was read
   * off the reader's own disk. In a shared view each one is its name, and nothing is asked.
   */
  const sharedView = useSharedViewer()
  /*
   * A ping chip for MY OWN agent opens that agent's conversation (owner,
   * 2026-09-21). Delegated on a wrapper rather than bound per chip, because the
   * chips are markdown output — there is no React element to give a handler to.
   * Only `.ping.mine` carries coordinates; a friend's is a name and nothing else.
   */
  const openDoc = useOpenDoc()
  const playable = !!openDoc && canPlayInApp()
  const play = playable ? (v: YouTubeVideo) => openDoc!({ url: v.url, name: 'YouTube', mime: '', youtube: v }) : null
  const onClick = (e: React.MouseEvent) => {
    /*
     * A YouTube link in the text plays in the app too, not only its picture below —
     * the link is what a person taps first.
     */
    const link = play ? (e.target as HTMLElement).closest?.('a[href]') : null
    const video = link ? youtubeVideo((link as HTMLAnchorElement).href) : null
    if (video && play) {
      e.preventDefault()
      play(video)
      return
    }
    const chip = (e.target as HTMLElement).closest?.('a.ping.mine')
    if (!chip) return
    e.preventDefault()
    const session = chip.getAttribute('data-agent-session') ?? ''
    if (session) ping?.open({ session, host: chip.getAttribute('data-agent-host') || null })
  }
  return (
    <div className="msg-prose" onClick={onClick}>
      {prose && <Markdown body={prose} {...(ping ? { ping: ping.resolve } : {})} />}
      {prose && <YouTubeCards text={prose} onPlay={play} />}
      {!sharedView &&
        shared.map((pf, i) =>
          pf.isImage ? (
            <ProjectImage key={`p${i}`} pf={pf} />
          ) : null,
        )}
      {files.map((f, i) => {
        if (sharedView) {
          return (
            <span key={i} className="msg-file off" title={f.name}>
              <Icon name="attach_file" size={13} />
              {f.name}
            </span>
          )
        }
        if (f.isImage && f.fileId) {
          return <MsgImage key={i} fileId={f.fileId} name={f.name} />
        }
        if (f.isImage) {
          return <PathImage key={i} path={f.path} name={f.name} />
        }
        return f.fileId ? (
          /* Fetch-on-click, not an href to a dead origin — the old link 404'd at
             the guard for the same reason the images did. */
          <button
            key={i}
            type="button"
            className="msg-file"
            onClick={() => {
              void noteFileBytes(f.fileId!).then((blob) => downloadBlob(blob, f.name))
            }}
          >
            <Icon name="attach_file" size={13} />
            {f.name}
          </button>
        ) : (
          <span key={i} className="msg-file off" title={f.path}>
            <Icon name="attach_file" size={13} />
            {f.name}
          </span>
        )
      })}
    </div>
  )
}

/** One message row — the agent's or the owner's. Lifted out of the render loop so
 *  the loop can group tool runs without carrying this inline. */
/**
 * Strings, not the AgentRow: the ['agents'] roster refetches on invalidation and
 * stream events (there is no interval — the 2026-08-30 trigger audit checked) and
 * hands back a new object each time, so an object prop would re-render every message
 * in the log per refetch, for a value that did not change. memo + primitives makes
 * an unchanged message free — and
 * a message that never re-renders is a message whose text node cannot be replaced
 * under a selection.
 */
/** Exported for tests: the attribution rule below is the owner-visible
 *  behaviour ('I don't want to see my own avatar talking with an agent tag'),
 *  and a unit test on the parser alone would not catch this component going
 *  back to treating every `user` block as the owner. */
/*
 * The person's tile used to be drawn HERE, by its own `initials()` — two letters on
 * the sender accent. It is identity/personAvatar.ts now (owner, 2026-09-13: 用 email
 * 首字 + 隨機背景的圓圈作為使用者頭像): one letter of the label (an address or a name)
 * on colours derived from it, the same tile the project chat and the account block
 * draw, so one person has one face across surfaces. The split-surrogate rule that
 * lived with the old function (2026-08-22: never `.slice()` a name — an emoji cut in
 * half is a lone surrogate) lives on in personLetter, which splits by code point.
 */

/**
 * A browser tab that rang for its agent — owner, 2026-10-12: 在 CleanView 的狀態下, 我們需要
 * Render Browser Activity … 知道他的該 tab 的 title 以及是從哪裡過來的.
 *
 * A margin note like the rest of the machinery: no avatar, no "you". Two things to say,
 * so two lines — WHICH TAB (the title the agent was told, or the bare label when nobody
 * on this side has looked at the tab yet), and under it WHAT HAPPENED, WHERE: the
 * notice's words turned to the reader, the site, the browser the tab is in, the label
 * the agent's own commands name it by (which is how a reader follows the tab through
 * the conversation), and when it rang — a notice may have waited for a busy agent.
 *
 * The title is the thing that was asked for, so it is never cut to one line: it wraps to
 * two, in its own direction (a right-to-left title cut as left-to-right loses its
 * beginning), and the whole of it is in the row's tooltip with the address.
 *
 * The words come from a context this row reads and `Msg` does not: see TabFactsContext.
 */
function BrowserActivityRow({ b, act, mark }: { b: TranscriptBlock; act: BrowserActivity; mark: string }) {
  const fact = useContext(TabFactsContext)?.get(b) ?? null
  const { title, under } = rowWords(act, fact)
  const hover = [title === act.tab ? '' : title, fact?.url ?? '', act.tab].filter(Boolean).join('\n')
  return (
    <div className={`sysnote bact${mark}`} title={hover}>
      {/* The bell is the extension's own mark for this switch. On a phone it stands alone:
          the words beside it took two fifths of the row from the title (chat.css). */}
      <span className="sysnote-label bact-label">
        <Icon name="notifications" size={13} />
        <span className="bact-label-text">Browser activity</span>
      </span>
      <span className="bact-main">
        <span className="bact-title" dir="auto">
          {title}
        </span>
        <span className="bact-what">
          {under}
          <BlockClock items={[b]} />
        </span>
      </span>
    </div>
  )
}

/**
 * A COMPACTION, FOLDED — owner, 2026-11-04: Claude 的 Agent 有時候會把訊息壓縮之後, 在 Clean View 下面會有一大串它壓縮的
 * 訊息打回它的 Context 裡面 … 人類不需要看到 … 只顯示一個短短的文字說訊息被壓縮了, 並且按下按鈕之後, 就可以展開來.
 *
 * When claude compacts, it writes the whole conversation so far as ONE user turn and reads it back as its new context.
 * Drawn as speech that was a wall of text under the owner's own face, words the owner never typed. A margin note now,
 * like the CLI's other machinery (systemNote): what happened, when, and the summary one press away.
 *
 * Decided by the edge's mark (`summary`), never by the text: the summary's opening words differ between claude
 * versions and sessions. It opens by itself when a search stands on it, so the word that was found is in sight; the
 * reader can still close it.
 */
function CompactedNote({ b, mark, current }: { b: TranscriptBlock; mark: string; current: boolean }) {
  const [open, setOpen] = useState(current)
  const [wasCurrent, setWasCurrent] = useState(current)
  if (current !== wasCurrent) {
    setWasCurrent(current)
    if (current) setOpen(true)
  }
  const bodyId = useId()
  return (
    <div className={`sysnote compacted${open ? ' open' : ''}${mark}`}>
      <div className="compacted-row">
        <span className="sysnote-label">Conversation compacted</span>
        <BlockClock items={[b]} />
        <button
          type="button"
          className="compacted-toggle"
          aria-expanded={open}
          {...(open ? { 'aria-controls': bodyId } : {})}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide summary' : 'Show summary'}
          <Icon name={open ? 'expand_less' : 'expand_more'} size={15} />
        </button>
      </div>
      {open && (
        <div id={bodyId} className="pmsg-body md compacted-body">
          <Markdown body={b.text ?? ''} />
        </div>
      )}
    </div>
  )
}

export const Msg = memo(function Msg({ b, session, hash, resolve, describeHash, describeRoom, ownerName, resolutionKey, belowHead, hit, current }: {
  b: TranscriptBlock
  session: string
  hash: string
  /** Search (owner, 2026-09-11): this block holds the keyword / is the one shown. Booleans, so memo still holds. */
  hit?: boolean
  current?: boolean
  /**
   * Rendered between the name row and the body of the agent's OWN message — the
   * turn's fold control lives here (owner, 2026-09-10: 把 Show the working 移動到
   * Agent 名稱的下方, 放置於上方很不直覺). Only the own-words branch has the slot;
   * an arrived message or a system note never carries a fold.
   */
  belowHead?: ReactNode
  /**
   * Label → channel hash, against the live roster. Must be REFERENTIALLY STABLE or
   * memo is defeated and every message re-renders on the 20s roster poll — which is
   * also what would replace text nodes under an active selection.
   */
  resolve: (label: string) => string | null
  /**
   * Channel hash → who that actually is, against the same roster (same stability
   * rule). An agent-msg tag is a BARE HASH by design — the wire keeps the identity
   * that never changes — but a person reading the Clean View should not be shown
   * `d021a1be5b0a` where "test1 @ MacBook Pro" is knowable.
   * Owner, 2026-08-07: 在 clean view 裡面把 Agent hash 解析出來. 成為
   * Agent name @ server owner name.
   *
   * `room`, when the message came out of a conversation, lets it name ANOTHER person's
   * agent from that room's roster; such an answer has no `machine` (useSenderResolution).
   */
  describeHash?: ((h: string, room?: string | null) => { name: string; machine: string } | null) | null
  /** Room id → the title this viewer knows it by, or null when they cannot name it. */
  describeRoom?: ((id: string) => string | null) | null
  /** The account owner — every agent this app can list belongs to them. */
  ownerName?: string | null
  /**
   * A fingerprint of what describeHash would answer, changing ONLY when the roster's
   * hash→identity mapping changes. Msg is memoized and its other props are kept
   * referentially stable on purpose (re-rendering on every 20s poll is what used to
   * drop text selections) — but that same stability FROZE the first resolution:
   * the transcript renders before the fleet query returns, every sender resolved to
   * null, and when the names arrived nothing told memo to look again. The owner
   * watched the fix ship and the screen not change (沒變, 還是 hash). The memo needs
   * exactly one thing in props that moves when the answer moves, and nothing that
   * moves when it does not.
   */
  resolutionKey?: string
}) {
  /* The cover-panel viewer above this transcript, when there is one (App provides
     it around AgentPanel). Read here, at the top, because a hook cannot sit inside
     the arrived-message branch that uses it. Stable, so memo is not disturbed. */
  const openDoc = useOpenDoc()
  // A friend reading a shared agent (sharedView.ts): null in the owner's own view.
  const viewer = useSharedViewer()
  /**
   * An arrived message is a `user` block, exactly like the owner's own typing —
   * that is why this view credited another agent's words to the owner, complete
   * with their avatar and the word "you". Parsing is what tells the two apart.
   */
  // Reasoning is not speech, and this is the first thing that has to be decided —
  // every branch below attributes the text to somebody.
  if (b.kind === 'thinking') return <ThinkingBlock b={b} />
  const mark = `${hit ? ' hit' : ''}${current ? ' hit-current' : ''}`

  /*
   * Anything this build does not recognise is shown WITHOUT an author.
   *
   * Adding `thinking` to the union fixed one instance of a more general fault: the
   * branch below is reached by elimination, so a kind nobody here has heard of gets
   * the agent's pet, its name and the badge "agent" — the renderer answering "who
   * said this?" with the nearest member rather than with "I don't know". A fifth CLI,
   * or one more block type from any of the four, would silently be quoted.
   *
   * avengents-server hit the same shape five times in one day on their side, and the
   * fix is the same: when the lookup fails, say so. The text is still shown, because
   * dropping it would leave a gap where something evidently happened.
   */
  if (b.kind !== 'user' && b.kind !== 'assistant') {
    return (
      <div className={`trun think${mark}`}>
        <div className="trun-hd trun-hd-static">
          <span className="trun-label">{b.kind}</span>
        </div>
        <pre className="tb-body">{b.text ?? ''}</pre>
      </div>
    )
  }

  // The CLI's summary of everything before it, read back as its context: folded to one line (CompactedNote below).
  // Before systemNote, because the summary quotes the conversation — reminders, commands, task notices — and one of
  // those could otherwise name the whole block.
  if (b.kind === 'user' && b.summary) return <CompactedNote b={b} mark={mark} current={!!current} />

  /*
   * Machinery before authorship, and BEFORE the markdown renderer ever sees the text.
   *
   *   owner: 你應該是要在「繪製」訊息元件的時候才把標籤加入,而非在整串訊息裡面
   *          先加入標籤才解析.
   *
   * Which is the rule this branch follows: the block is CLASSIFIED here, at render, and
   * the string is never rewritten. A task-notification is not prose, so it does not go
   * through a prose renderer at all — the previous behaviour handed `<task-notification>`
   * to markdown and let the escaping decide what a reader saw.
   */
  const note = b.kind === 'user' ? systemNote(b.text) : null
  if (note) {
    return (
      <div className={`sysnote${mark}`}>
        <span className="sysnote-label">{note.label}</span>
        {note.detail && <span className="sysnote-detail">{note.detail}</span>}
      </div>
    )
  }

  // A tab ringing for its agent ("Notify agent") is machinery too — but it has a subject,
  // the tab, and that is what its row says (agents/browserActivity.ts).
  const rang = tabNotice(b)
  if (rang) return <BrowserActivityRow b={b} act={rang} mark={mark} />

  const from = b.kind === 'user' ? parseInbound(b.text) : null
  const mine = b.kind === 'user' && !from
  const accent = agentColor(hash)

  if (from) {
    /*
     * `__owner__` is the edge's sentinel for a message the OWNER sent through the
     * messaging lane — the UI composer, or a server nudge written on their behalf.
     * It is not an address and no roster will ever resolve it; drawn through the
     * agent path it became a stranger named "__owner__" with a generated pet,
     * wearing the owner's own words. Their voice, their styling.
     */
    // The reader's OWN words, when a friend reads a shared agent: theirs, with their letter.
    if (from.kind === 'friend' && viewer?.selfEmail && from.email?.toLowerCase() === viewer.selfEmail.toLowerCase()) {
      return (
        <div className={`pmsg${mark}`}>
          <span
            className="pmsg-avatar"
            style={{ background: 'var(--c89b4fa)', color: 'var(--c11111b)' }}
            aria-hidden="true"
          >
            {personLetter(viewer.selfLabel)}
          </span>
          <div className="pmsg-main">
            <div className="pmsg-head">
              <span className="pmsg-name">you</span>
              <span className="pmsg-badge you">you</span>
              <BlockClock items={[b]} />
            </div>
            <div className="pmsg-body md">
              <MsgBody text={from.body} />
            </div>
          </div>
        </div>
      )
    }
    if (from.kind === 'agent' && from.hash === OWNER_SENDER) {
      return (
        <div className={`pmsg${mark}`}>
          <span
            className="pmsg-avatar"
            style={{ background: 'var(--c89b4fa)', color: 'var(--c11111b)' }}
            aria-hidden="true"
          >
            YT
          </span>
          <div className="pmsg-main">
            <div className="pmsg-head">
              <span className="pmsg-name">you</span>
              <span className="pmsg-badge you">you</span>
              <BlockClock items={[b]} />
            </div>
            <div className="pmsg-body md">
              <MsgBody text={from.body} />
            </div>
            <BookmarkButton b={b} who="you" kind="you" text={from.body} />
          </div>
        </div>
      )
    }
    // For an agent-msg the hash is in the framing itself; a project-msg carries only
    // a label, so it is looked up. Unresolved ⇒ a plain tile, never a pet seeded on
    // the label — that would give one agent two different faces in one app.
    //
    // A chat-msg with no hash is a PERSON, and its label is their display name. It is
    // deliberately not looked up: `resolveSender` matches names against the agent
    // roster, so a human called after one of the machines' sessions would be handed
    // that agent's face and colour. A person gets a plain tile, always.
    const senderHash = from.hash ?? (from.kind === 'chat' || from.kind === 'friend' ? null : resolve(from.label))
    const senderAccent = senderHash ? agentColor(senderHash) : 'var(--c89dceb)'
    /*
     * The hash resolved to a person-readable identity, when the roster knows it.
     * Unresolved stays the raw hash — honest, and it happens legitimately: an agent
     * killed after sending, or one on a machine that has not synced. The hash also
     * survives in the tooltip when resolved, because it is the reply address and the
     * one part that never changes.
     */
    void resolutionKey // memo compares it; the value itself is not used
    const who = from.hash ? (describeHash?.(from.hash, from.room ?? null) ?? null) : null
    return (
      <div className={`pmsg pmsg-in${mark}`} style={{ ['--in-accent' as string]: senderAccent }}>
        {senderHash ? (
          <span className="pmsg-avatar pet">
            {/* 30 in a 38px frame — the clearance rule, same as below. */}
            <PixelPet name={who?.name ?? from.label} seed={senderHash} size={30} />
          </span>
        ) : (
          <span
            className="pmsg-avatar"
            style={{ background: personBackground(from.label), color: 'var(--c11111b)' }}
            aria-hidden="true"
          >
            {personLetter(from.label)}
          </span>
        )}
        <div className="pmsg-main">
          <div className="pmsg-head">
            <span className="pmsg-name" style={{ color: senderAccent }} title={from.hash ?? undefined}>
              {who?.name ?? from.label}
            </span>
            {/* No machine: another person's agent, named by the room it spoke in. */}
            {who?.machine && <span className="pmsg-at">@ {who.machine}</span>}
            {/*
              * Who it spoke FOR.
              *
              * A chat frame names both halves — the agent said it, under a person's
              * account — so that person is named here rather than this app's owner.
              * `ownerName` is the right answer only when the frame did not say: it
              * is who the RECEIVING machine belongs to, which for a chat room with
              * several people in it is a guess that reads like a fact.
              */}
            {from.spokenFor ? (
              <span className="pmsg-owner">{from.spokenFor}</span>
            ) : (
              who?.machine && ownerName && <span className="pmsg-owner">{ownerName}</span>
            )}
            {/* Says HOW it arrived. A project message, a chat message and a direct
                one look identical once the framing is stripped, and which room
                something was said in is often the whole context. */}
            <span className={`pmsg-badge ${from.kind === 'chat' || from.kind === 'friend' ? 'chat' : 'agent'}`}>
              {from.kind === 'project' ? 'project' : from.kind === 'chat' ? 'chat' : from.kind === 'friend' ? 'friend' : 'agent'}
            </span>
            {/*
              * WHICH room — owner, 2026-08-22: 要把 project chat 的 title 放進去.
              *
              * Beside the badge, because the two answer one question together: how it
              * arrived, and where from. Rendered only when this viewer can NAME the
              * room; an agent can be attached to a conversation its owner is not in,
              * and sixteen hex digits across the head would cost a line to say
              * nothing. The id stays in the tooltip, where it is the reply address it
              * always was.
              */}
            {(() => {
              const room = from.room ? (describeRoom?.(from.room) ?? null) : null
              if (!room) return null
              return (
                <span className="pmsg-room" title={`${room} · ${from.room}`}>
                  {room}
                </span>
              )
            })()}
            {/* An arrived message's clock is the moment it LANDED in this
                transcript, not when the sender wrote it — the wire carries only
                the receiving record's own time, and claiming otherwise would put
                a queued-for-an-hour message under the hour it was written. */}
            <BlockClock items={[b]} />
          </div>
          <div className="pmsg-body md">
            <MsgBody text={from.body} />
          </div>
          {/* A chat message can BE a file — its body is then a card, not prose, and
              unparsed it showed the reader a line of JSON. The caption is above;
              this is what came with it.

              SHOW THE PICTURE, not just its name — owner, 2026-08-22: 目前 Clean View
              無法解析 Chat 來的 image. A screenshot arrived as a filename chip here
              while Project Chat rendered it, and a screenshot you cannot see is the
              one thing a screenshot is for. Same component as Project Chat rather
              than a second renderer: one place decides what an attachment looks
              like, so the two surfaces cannot drift apart again.

              The chip stays as the fallback, and it is reached in two real cases: a
              card that carried no `file_id` (older senders), and the E2E lane, where
              the bytes need a project the conversation id is not. On DynamoDB the id
              alone is enough — `dynamoAttachmentBytes` ignores the conversation. */}
          {from.files && (
            <div className="pmsg-files">
              {from.files.map((f, i) =>
                /* Not in a friend's view (review of 2026-10-17): the card presigns on the
                   READER's own chat account for an id from the sharer's side — the name only. */
                f.file_id && from.room && !viewer ? (
                  <AttachmentCard
                    key={f.file_id}
                    att={{
                      file_id: f.file_id,
                      name: f.name,
                      // Spread-when-present, not `mime: f.mime`: under
                      // exactOptionalPropertyTypes an explicit `undefined` is a
                      // different thing from an absent key, and the card reads
                      // absence as "unknown" rather than as an empty value.
                      ...(f.mime ? { mime: f.mime } : {}),
                      ...(f.size !== undefined ? { size: f.size } : {}),
                      ...(f.thumb ? { thumb: f.thumb } : {}),
                    }}
                    projectId={from.room}
                    /* It ARRIVED — someone else sent it. `mine` would claim the
                       bytes are already local, which is only true for our uploads. */
                    /* The cover-panel viewer when this transcript has one above it
                       (it does, since 2026-09-13 — see files/openDoc.ts); a new tab
                       only where nothing provides one. */
                    onOpenDoc={openDoc ?? ((a) => window.open(a.url, '_blank', 'noopener'))}
                  />
                ) : (
                  <span className="pmsg-file" key={`${f.name}-${i}`} title={f.name}>
                    <Icon name="attach_file" size={11} />
                    {f.name}
                  </span>
                ),
              )}
            </div>
          )}
          <BookmarkButton b={b} who={who?.name ?? from.label} kind="inbound" text={from.body} />
        </div>
      </div>
    )
  }

  if (mine && viewer) {
    return (
      <div className={`pmsg${mark}`}>
        <span
          className="pmsg-avatar"
          style={{ background: personBackground(viewer.ownerLabel), color: 'var(--c11111b)' }}
          aria-hidden="true"
        >
          {personLetter(viewer.ownerLabel)}
        </span>
        <div className="pmsg-main">
          <div className="pmsg-head">
            <span className="pmsg-name">{viewer.ownerLabel}</span>
            <span className="pmsg-badge chat">owner</span>
            <BlockClock items={[b]} />
          </div>
          <div className="pmsg-body md">
            <MsgBody text={b.text ?? ''} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`pmsg${mark}`}>
      {mine ? (
        <span
          className="pmsg-avatar"
          style={{ background: 'var(--c89b4fa)', color: 'var(--c11111b)' }}
          aria-hidden="true"
        >
          YT
        </span>
      ) : (
        <span className="pmsg-avatar pet">
          {/*
            30, not 38, inside the same 38px frame. PixelPet scales its whole body
            by 1.25 about the centre, so it deliberately paints OUTSIDE its viewBox —
            the tallest accessory (the antenna's ball) reaches y = -8.25 of 64. The
            frame sets overflow:hidden, so at 38px that ornament was cut off entirely
            and the body's outline was flattened against the sides.

            Fits when (38 - S)/2 >= 8.25 * S/64, i.e. S <= 30.2.

            Read off the accessory geometry rather than sampled by eye: the four pets
            I first looked at all wear the horns, which top out at -3.6 and clear 34px.
            Choosing by that picture would have shipped 32 and still clipped every pet
            wearing the antenna.
          */}
          <PixelPet name={session} seed={hash} size={30} />
        </span>
      )}
      <div className="pmsg-main">
        <div className="pmsg-head">
          <span className="pmsg-name" style={mine ? undefined : { color: accent }}>
            {mine ? 'you' : session}
          </span>
          <span className={`pmsg-badge ${mine ? 'you' : 'agent'}`}>{mine ? 'you' : 'agent'}</span>
          <BlockClock items={[b]} />
        </div>
        {belowHead}
        <div className="pmsg-body md">
          <MsgBody text={b.text ?? ''} />
        </div>
        <BookmarkButton b={b} who={mine ? 'you' : session} kind={mine ? 'you' : 'agent'} text={b.text ?? ''} />
      </div>
    </div>
  )
})
